import { type SetStateAction, useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Icon, Image, Label, Popup } from "semantic-ui-react"
import { GiphyGif } from "simplydo/react/components/Giphy/Gif"
import { GiphyPicker } from "simplydo/react/components/Giphy/Picker"
import { MentionText } from "simplydo/react/richtext/MentionText"
import { styled } from "styled-components"

import { FileChooser } from "components/lib/Choosers"

import type { CommentState } from "./Discussion"

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 0.5px solid #c9c9c9;
  border-radius: 5px;
  background-color: #fff;
  padding: 6px 3px;
`

export const MediaContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1px;
  align-items: flex-start;
  justify-content: flex-start;

  margin: 5px 0;
`

const ActionArea = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 5px 0 5px;
  border-top: 0.5px solid silver;
  padding-top: 7px;
  padding-bottom: 2px;

  > div {
    display: flex;
    align-items: center;
  }
  > .ui.button {
    margin: 0;
  }

`

type CommentType = CommentState["string"]
type CommentInputProps = {
  inputRef: React.RefObject<any>
  forType: string
  forId: string
  currentReply: CommentType
  updateReply: (commentOrUpdater: CommentType | SetStateAction<CommentType>) => void
  postComment: () => void
  canPostMedia?: boolean
}

export const CommentInput = ({
  inputRef,
  forType,
  forId,
  currentReply,
  updateReply,
  postComment,
  canPostMedia,
}: CommentInputProps) => {
  const [giphyPickerOpen, setGiphyPickerOpen] = useState(false)
  const { t } = useTranslation()

  const handleAddAttachment = useCallback(
    (attachment) => {
      updateReply((prevState) => {
        const updatedAttachments = [...(prevState.media || []), attachment]
        return {
          ...prevState,
          media: updatedAttachments,
        }
      })
    },
    [updateReply],
  )

  const addAttachment = useCallback(
    (storedName, attachmentName, signedDownloadRequest) => {
      handleAddAttachment({
        value: storedName,
        name: attachmentName,
        signedDownloadRequest,
        type: attachmentName.match(/\.(jpg)|(png)|(gif)|(jpeg)$/i) ? "image" : "file",
      })
    },
    [handleAddAttachment],
  )

  const addGiphy = useCallback(
    (gif) => {
      handleAddAttachment({
        value: gif.url,
        name: gif.title,
        type: "gif",
        data: gif,
      })
    },
    [handleAddAttachment],
  )

  const deleteAttachment = useCallback(
    (index) => {
      updateReply((prevState) => {
        return {
          ...prevState,
          media: prevState.media?.filter((_att, attIndex) => attIndex !== index) || [],
        }
      })
    },
    [updateReply],
  )

  return (
    <InputContainer>
      <MentionText
        editorRef={inputRef}
        placeholder="Write a comment..."
        value={currentReply.message}
        onChange={(value) => updateReply((prev) => ({ ...prev, message: value }))}
      />

      <MediaContainer>
        {currentReply.media.map((file, index) => {
          switch (file.type) {
            case "gif":
              return (
                <GiphyGif
                  onClickGif={() => deleteAttachment(index)}
                  canDelete={true}
                  key={file.value}
                  gif={file.data}
                  size="fixed_small"
                />
              )
            case "image":
              return (
                <div style={{ position: "relative" }} key={file.value}>
                  <Image
                    bordered
                    rounded
                    spaced
                    src={file.signedDownloadRequest}
                    width={75}
                    height={75}
                    style={{ objectFit: "cover" }}
                  />
                  <Icon
                    name="close"
                    onClick={() => deleteAttachment(index)}
                    style={{
                      cursor: "pointer",
                      position: "absolute",
                      top: 3,
                      right: 8,
                    }}
                  />
                </div>
              )
            default:
              return (
                <Label key={file.value}>
                  {file.name} <Icon name="close" onClick={() => deleteAttachment(index)} />
                </Label>
              )
          }
        })}
      </MediaContainer>

      <ActionArea>
        <div>
          <Popup
            on="hover"
            trigger={
              <Button
                basic
                icon="user"
                onClick={(e) => {
                  e.preventDefault()
                  const newComment = `${currentReply.message}@`
                  updateReply((prev) => ({ ...prev, message: newComment }))
                  setTimeout(() => {
                    // @ts-ignore
                    inputRef.current?.focus?.(10000)
                  }, 200)
                }}
              />
            }
            content={t("feed.mention")}
          />
          {canPostMedia ? (
            <>
              <FileChooser
                forType={forType}
                forId={forId}
                trigger={<Button basic icon="paperclip" />}
                onComplete={addAttachment}
              />
              <GiphyPicker
                onChoose={(gif) => {
                  addGiphy(gif)
                  setGiphyPickerOpen(false)
                }}
                trigger={
                  <Popup
                    on="hover"
                    trigger={<Button basic icon="file image outline" onClick={() => setGiphyPickerOpen(true)} />}
                    content={t("feeds.attachments.gif")}
                  />
                }
                open={giphyPickerOpen}
                onClose={() => setGiphyPickerOpen(false)}
              />
            </>
          ) : null}
        </div>
        <Popup
          on="hover"
          trigger={<Button basic icon="send" onClick={() => postComment()} loading={currentReply.loading} />}
          content={t("feeds.post")}
          disabled={!currentReply.message && !currentReply.media.length}
        />
      </ActionArea>
    </InputContainer>
  )
}
